import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import axios from 'axios';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import { IndicatorChart } from '../features/objectives/IndicatorChart';
import { IndicatorFormModal } from '../features/objectives/IndicatorFormModal';
import { ObjectiveFormModal } from '../features/objectives/ObjectiveFormModal';
import {
  createIndicator,
  createIndicatorMeasurement,
  createObjectiveTask,
  escalateIndicatorToNc,
  fetchIndicatorMeasurements,
  fetchObjective,
  updateIndicator,
  updateObjective,
  updateObjectiveTask,
} from '../features/objectives/objectivesApi';
import type { Indicator, IndicatorCategory, IndicatorMeasurement, MeasurementPayload, QualityObjective } from '../features/objectives/types';
import {
  INDICATOR_CATEGORY_LABEL,
  OBJECTIVE_STATUS_LABEL,
  extractFormulaVariables,
  formatMeasurementPeriodLabel,
  progressBarClass,
  resolveIndicatorCategory,
  withinTargetBadgeClass,
  withinTargetLabel,
} from '../features/objectives/objectiveUtils';
import { DateCell } from '../components/DateCell';

export function ObjectiveDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const { confirm } = useConfirmDialog();
  const { canEdit } = usePlatformAccessLevel('objectives.access');
  const [objective, setObjective] = useState<QualityObjective | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [indicatorModalOpen, setIndicatorModalOpen] = useState(false);
  const [editingIndicator, setEditingIndicator] = useState<Indicator | null>(null);
  const [selectedIndicatorId, setSelectedIndicatorId] = useState<string | null>(null);
  const [measurements, setMeasurements] = useState<IndicatorMeasurement[]>([]);
  const [seriesMeta, setSeriesMeta] = useState<Indicator | null>(null);
  const [taskDescription, setTaskDescription] = useState('');
  const [measurementPeriodStart, setMeasurementPeriodStart] = useState('');
  const [measurementPeriodEnd, setMeasurementPeriodEnd] = useState('');
  const [measurementValue, setMeasurementValue] = useState('');
  const [formulaContext, setFormulaContext] = useState<Record<string, string>>({});
  const [measurementError, setMeasurementError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<'' | IndicatorCategory>('');

  const filteredIndicators = useMemo(() => {
    const all = objective?.indicators ?? [];
    if (!categoryFilter) return all;
    return all.filter((ind) => resolveIndicatorCategory(ind.category) === categoryFilter);
  }, [objective?.indicators, categoryFilter]);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchObjective(id);
      setObjective(data);
      const firstIndicator = data.indicators?.[0];
      if (firstIndicator && !selectedIndicatorId) {
        setSelectedIndicatorId(firstIndicator.id);
      }
    } catch {
      setError('No se pudo cargar el objetivo.');
    } finally {
      setLoading(false);
    }
  }, [id, selectedIndicatorId]);

  const loadSeries = useCallback(async (indId: string) => {
    try {
      const series = await fetchIndicatorMeasurements(indId);
      setMeasurements(series.measurements);
      setSeriesMeta(series.indicator as Indicator);
    } catch {
      setMeasurements([]);
      setSeriesMeta(null);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (selectedIndicatorId) {
      void loadSeries(selectedIndicatorId);
    }
  }, [selectedIndicatorId, loadSeries]);

  useEffect(() => {
    setFormulaContext({});
    setMeasurementError(null);
    setMeasurementPeriodStart('');
    setMeasurementPeriodEnd('');
  }, [selectedIndicatorId]);

  const selectedIndicator = objective?.indicators?.find((i) => i.id === selectedIndicatorId);
  const formulaVariables = useMemo(
    () => (selectedIndicator?.formula?.trim() ? extractFormulaVariables(selectedIndicator.formula) : []),
    [selectedIndicator?.formula],
  );

  const handleAddMeasurement = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedIndicatorId || !measurementPeriodStart || !measurementPeriodEnd) {
      setMeasurementError('Indique la fecha de inicio y fin del período.');
      return;
    }
    if (measurementPeriodStart > measurementPeriodEnd) {
      setMeasurementError('La fecha de inicio no puede ser posterior a la fecha de fin.');
      return;
    }
    const indicator = objective?.indicators?.find((i) => i.id === selectedIndicatorId);
    if (!indicator) return;

    const hasFormula = Boolean(indicator.formula?.trim());
    setMeasurementError(null);

    const payload: MeasurementPayload = {
      periodLabel: formatMeasurementPeriodLabel(measurementPeriodStart, measurementPeriodEnd),
    };

    if (hasFormula) {
      const ctx: Record<string, number> = {};
      for (const variable of formulaVariables) {
        const raw = formulaContext[variable];
        const num = Number.parseFloat(String(raw ?? ''));
        if (!Number.isFinite(num)) {
          setMeasurementError(`Complete un valor numérico para "${variable}".`);
          return;
        }
        ctx[variable] = num;
      }
      if (formulaVariables.length > 0) {
        payload.formulaContext = ctx;
      }
    } else {
      const num = Number.parseFloat(measurementValue);
      if (!Number.isFinite(num)) {
        setMeasurementError('Indique un valor numérico válido.');
        return;
      }
      payload.value = num;
    }

    setSubmitting(true);
    try {
      await createIndicatorMeasurement(selectedIndicatorId, payload);
      setMeasurementValue('');
      setFormulaContext({});
      await loadSeries(selectedIndicatorId);
      await load();
    } catch (err) {
      const message =
        axios.isAxiosError(err) && typeof err.response?.data?.error === 'string'
          ? err.response.data.error
          : err instanceof Error
            ? err.message
            : 'No se pudo registrar la medición.';
      setMeasurementError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !taskDescription.trim()) return;
    setSubmitting(true);
    try {
      await createObjectiveTask(id, { description: taskDescription.trim() });
      setTaskDescription('');
      await load();
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleTask = async (taskId: string, done: boolean) => {
    await updateObjectiveTask(taskId, { done });
    await load();
  };

  const handleEscalate = async (indicator: Indicator) => {
    if (indicator.qmsRecordId) return;
    const ok = await confirm({
      title: 'Escalar a No Conformidad',
      message: '¿Escalar este indicador a una No Conformidad en QMS?',
      confirmText: 'Escalar a NC',
      variant: 'danger',
    });
    if (!ok) return;
    setSubmitting(true);
    try {
      await escalateIndicatorToNc(indicator.id);
      await load();
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !objective) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
      </div>
    );
  }

  if (error || !objective) {
    return (
      <div className="p-6">
        <p className="text-destructive">{error ?? 'Objetivo no encontrado'}</p>
        <Link to="/objetivos" className="text-primary text-sm mt-2 inline-block">
          Volver a objetivos
        </Link>
      </div>
    );
  }

  const progress = objective.progress ?? { done: 0, total: 0, percent: 0 };
  const latestMeasurement = selectedIndicator?.measurements?.[0];
  const isCalculatedIndicator = Boolean(selectedIndicator?.formula?.trim());

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link to="/objetivos" className="text-sm text-muted-foreground hover:text-primary">
            ← Objetivos
          </Link>
          <h1 className="text-2xl font-bold mt-1">
            {objective.code} — {objective.title}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">{objective.target}</p>
        </div>
        {canEdit && (
          <button type="button" className="btn btn-secondary" onClick={() => setEditOpen(true)}>
            Editar
          </button>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <p className="text-xs text-muted-foreground">Estado</p>
          <p className="font-medium">{OBJECTIVE_STATUS_LABEL[objective.status]}</p>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <p className="text-xs text-muted-foreground">Período</p>
          <p className="font-medium">
            <DateCell value={objective.periodStart} /> — <DateCell value={objective.periodEnd} />
          </p>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <p className="text-xs text-muted-foreground">Progreso tareas</p>
          <div className="flex items-center gap-2 mt-1">
            <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full ${progressBarClass(progress.percent)}`}
                style={{ width: `${progress.percent}%` }}
              />
            </div>
            <span className="text-sm">
              {progress.done}/{progress.total}
            </span>
          </div>
        </div>
      </div>

      {objective.relatedRisk && (
        <div className="bg-card border border-border rounded-lg p-4 text-sm">
          <span className="text-muted-foreground">Riesgo relacionado: </span>
          <Link to={`/riesgos/${objective.relatedRisk.id}`} className="text-primary hover:underline">
            {objective.relatedRisk.code} — {objective.relatedRisk.title}
          </Link>
        </div>
      )}

      <section className="bg-card border border-border rounded-lg p-4 space-y-4">
        <h2 className="font-semibold">Tareas planificadas</h2>
        <ul className="space-y-2">
          {(objective.tasks ?? []).map((task) => (
            <li key={task.id} className="flex items-start gap-3 text-sm border-b border-border pb-2">
              {canEdit ? (
                <input
                  type="checkbox"
                  checked={task.done}
                  onChange={(e) => void handleToggleTask(task.id, e.target.checked)}
                  className="mt-1"
                />
              ) : (
                <span>{task.done ? '✓' : '○'}</span>
              )}
              <div className="flex-1">
                <p className={task.done ? 'line-through text-muted-foreground' : ''}>{task.description}</p>
                {task.dueDate && (
                  <p className="text-xs text-muted-foreground">
                    Vence: <DateCell value={task.dueDate} />
                  </p>
                )}
              </div>
            </li>
          ))}
          {(objective.tasks ?? []).length === 0 && (
            <li className="text-sm text-muted-foreground">Sin tareas registradas.</li>
          )}
        </ul>
        {canEdit && (
          <form onSubmit={handleAddTask} className="flex gap-2">
            <input
              className="flex-1 rounded-md border border-border px-3 py-2 text-sm"
              placeholder="Nueva tarea…"
              value={taskDescription}
              onChange={(e) => setTaskDescription(e.target.value)}
            />
            <button type="submit" className="btn btn-primary btn-sm" disabled={submitting}>
              Agregar
            </button>
          </form>
        )}
      </section>

      <section className="bg-card border border-border rounded-lg p-4 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-semibold">Indicadores (KPI)</h2>
          <div className="flex flex-wrap items-center gap-2">
            <select
              className="rounded-md border border-border px-2 py-1 text-sm"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value as '' | IndicatorCategory)}
              aria-label="Filtrar por categoría"
            >
              <option value="">Todas las categorías</option>
              <option value="calidad">{INDICATOR_CATEGORY_LABEL.calidad}</option>
              <option value="ambiental">{INDICATOR_CATEGORY_LABEL.ambiental}</option>
            </select>
            {canEdit && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => {
                  setEditingIndicator(null);
                  setIndicatorModalOpen(true);
                }}
              >
                Nuevo indicador
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {filteredIndicators.map((ind) => {
            const latest = ind.measurements?.[0];
            return (
              <button
                key={ind.id}
                type="button"
                onClick={() => setSelectedIndicatorId(ind.id)}
                className={`px-3 py-2 rounded-md border text-sm text-left ${
                  selectedIndicatorId === ind.id
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:bg-muted/50'
                }`}
              >
                <span className="font-medium">{ind.name}</span>
                <span className="ml-2 inline-flex px-2 py-0.5 rounded-full text-xs bg-muted text-muted-foreground">
                  {INDICATOR_CATEGORY_LABEL[resolveIndicatorCategory(ind.category)]}
                </span>
                {latest && (
                  <span
                    className={`ml-2 inline-flex px-2 py-0.5 rounded-full text-xs ${withinTargetBadgeClass(latest.withinTarget)}`}
                  >
                    {withinTargetLabel(latest.withinTarget)}
                  </span>
                )}
              </button>
            );
          })}
          {filteredIndicators.length === 0 && (
            <p className="text-sm text-muted-foreground">Sin indicadores.</p>
          )}
        </div>

        {selectedIndicator && (
          <div className="space-y-4 pt-2 border-t border-border">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm text-muted-foreground">
                {isCalculatedIndicator
                  ? 'Indicador calculado (DSL)'
                  : 'Carga manual'}
                {selectedIndicator.unit ? ` · ${selectedIndicator.unit}` : ''}
              </div>
              <div className="flex gap-2">
                {canEdit && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => {
                      setEditingIndicator(selectedIndicator);
                      setIndicatorModalOpen(true);
                    }}
                  >
                    Editar
                  </button>
                )}
                {canEdit && latestMeasurement && !latestMeasurement.withinTarget && (
                  <>
                    {selectedIndicator.qmsRecordId ? (
                      <Link
                        to={`/registros/no-conformidades/${selectedIndicator.qmsRecordId}`}
                        className="btn btn-secondary btn-sm"
                      >
                        Ver NC
                      </Link>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        disabled={submitting}
                        onClick={() => void handleEscalate(selectedIndicator)}
                      >
                        Escalar a NC
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>

            <IndicatorChart
              measurements={measurements}
              targetValue={seriesMeta?.targetValue}
              lowerLimit={seriesMeta?.lowerLimit}
              upperLimit={seriesMeta?.upperLimit}
            />

            {canEdit && (
              <form onSubmit={handleAddMeasurement} className="space-y-3">
                <div className="flex flex-wrap gap-2 items-end">
                  <div>
                    <label className="text-xs text-muted-foreground">Inicio período</label>
                    <input
                      type="date"
                      className="block rounded-md border border-border px-3 py-2 text-sm"
                      value={measurementPeriodStart}
                      onChange={(e) => setMeasurementPeriodStart(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Fin período</label>
                    <input
                      type="date"
                      className="block rounded-md border border-border px-3 py-2 text-sm"
                      value={measurementPeriodEnd}
                      min={measurementPeriodStart || undefined}
                      onChange={(e) => setMeasurementPeriodEnd(e.target.value)}
                      required
                    />
                  </div>
                  {!isCalculatedIndicator && (
                    <div>
                      <label className="text-xs text-muted-foreground">Valor</label>
                      <input
                        type="number"
                        step="any"
                        className="block rounded-md border border-border px-3 py-2 text-sm"
                        value={measurementValue}
                        onChange={(e) => setMeasurementValue(e.target.value)}
                        required
                      />
                    </div>
                  )}
                  {isCalculatedIndicator &&
                    formulaVariables.map((variable) => (
                      <div key={variable}>
                        <label className="text-xs text-muted-foreground">{variable}</label>
                        <input
                          type="number"
                          step="any"
                          className="block rounded-md border border-border px-3 py-2 text-sm"
                          value={formulaContext[variable] ?? ''}
                          onChange={(e) =>
                            setFormulaContext((prev) => ({ ...prev, [variable]: e.target.value }))
                          }
                          required
                        />
                      </div>
                    ))}
                  <button type="submit" className="btn btn-primary btn-sm" disabled={submitting}>
                    {submitting ? 'Guardando…' : 'Registrar medición'}
                  </button>
                </div>
                {isCalculatedIndicator && formulaVariables.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    El valor se calculará automáticamente a partir de la fórmula configurada.
                  </p>
                )}
                {measurementError && (
                  <p className="text-sm text-destructive" role="alert">
                    {measurementError}
                  </p>
                )}
              </form>
            )}
          </div>
        )}
      </section>

      <ObjectiveFormModal
        open={editOpen}
        onOpenChange={setEditOpen}
        initial={objective}
        onSubmit={async (payload) => {
          await updateObjective(id, payload);
          await load();
        }}
      />

      <IndicatorFormModal
        open={indicatorModalOpen}
        onOpenChange={setIndicatorModalOpen}
        objectiveId={id}
        initial={editingIndicator}
        onSubmit={async (payload) => {
          if (editingIndicator) {
            await updateIndicator(editingIndicator.id, payload);
          } else {
            await createIndicator(payload);
          }
          await load();
          setEditingIndicator(null);
        }}
      />
    </div>
  );
}
